import React from "react";

import _ from "lodash";

import {
    CreateTableFilterInterface,
    DeleteTableFilterInterface,
    GetSavedTableFilterInterface,
    SetFavoriteTableFilterInterface,
} from "commons/types/api/tableFilter";
import {
    CalculateFiltersDescriptionInterface,
    FilterField,
    FilterFieldCollection,
    FilterOperation,
    FilterType,
    TableFilterType,
} from "commons/types/table";

import { getValueByPath } from "commons/utils/form";
import storage from "commons/utils/storage";

export type PartialFormType = {
    [y: string]: any;
};

type FormType = {
    [x: string]: PartialFormType;
};

const getFilterObject = (
    form: PartialFormType,
    fieldObj: FilterField,
    fieldKey: string
): FilterType | null => {
    if (fieldObj.operation) {
        return {
            key: fieldObj.key,
            operation: fieldObj.operation,
            value: getValueByPath(form, fieldKey),
        };
    } else {
        return null;
    }
};

const computeOrFilterValues = (
    field: FilterField,
    form: PartialFormType,
    key: string
): Array<FilterType> => {
    const filterValues: Array<FilterType> = [];

    _.forEach(field.fields, (fieldValueObject, collectionKey) => {
        if (fieldValueObject.operation === FilterOperation.And || fieldValueObject.operation === FilterOperation.Or) {
            const filterValuesTmp = computeOrFilterValues(
                fieldValueObject,
                form,
                `${key}.${collectionKey}`
            );
            if (filterValuesTmp.length) {
                filterValues.push({
                    operation: FilterOperation.And,
                    value: filterValuesTmp,
                });
            }
        } else {
            // deve essere settato
            if (getValueByPath(form, `${key}.${collectionKey}`)) {
                const filterObj = getFilterObject(
                    form,
                    fieldValueObject,
                    `${key}.${collectionKey}`
                );

                // Se torna un valore allora lo aggiungo all'array
                filterObj !== null && filterValues.push(filterObj);
            }
        }
    });

    return filterValues;
};

export const createTableFilters = (
    fields: FilterFieldCollection,
    form: PartialFormType
): Array<FilterType> => {
    const filters: Array<FilterType> = [];

    _.forEach(fields, (field, key) => {
        if (
            !field.dontFilter && // deve essere possibile filtrare per questo campo
            form &&
            (form[key] || (field.canBeFalse && form[key] === false)) && // deve essere settato
            (!Array.isArray(form[key]) || form[key].length > 0) // non deve essere un array oppure deve avere almeno un elemento
        ) {
            if (field.operation === FilterOperation.Or || field.operation === FilterOperation.And) {
                const filterValues: Array<FilterType> = computeOrFilterValues(field, form, key);

                if (filterValues.length) {
                    const typeOrFilter = {
                        operation: field.operation,
                        value: filterValues,
                    };

                    filters.push(typeOrFilter);
                }
            } else {
                const filterObj = getFilterObject(form, field, key);

                // Se torna un valore allora lo aggiungo all'array
                filterObj !== null && filters.push(filterObj);
            }
        }
    });

    return filters;
};

export const calculateDefaultValue = (fields: FilterFieldCollection): PartialFormType => {
    const defaultValueCalculated: PartialFormType = {};

    _.forEach(fields, (field, key) => {
        if (field.operation === FilterOperation.Or || field.operation === FilterOperation.And) {
            if (!(key in defaultValueCalculated)) {
                defaultValueCalculated[key] = calculateDefaultValue(field.fields ?? {});
            }
        } else {
            defaultValueCalculated[key] = field.defaultValue;
        }
    });

    return defaultValueCalculated;
};

export type HookParamsType = {
    filterGroup: string;
    defaultFilterType: string;
    fields: { [z: string]: FilterFieldCollection };
    getSavedTableFilter: GetSavedTableFilterInterface;
    setFavoriteTableFilter: SetFavoriteTableFilterInterface;
    deleteTableFilter: DeleteTableFilterInterface;
    createTableFilter: CreateTableFilterInterface;
    calculateFiltersDescription: CalculateFiltersDescriptionInterface;
};

function useTableFilterBase({
    filterGroup,
    defaultFilterType,
    fields,
    getSavedTableFilter,
    setFavoriteTableFilter,
    deleteTableFilter,
    createTableFilter,
    calculateFiltersDescription,
}: HookParamsType) {
    const defaultValue = React.useCallback(() => {
        const initialValue: FormType = {};

        _.forEach(fields, (realFields, fieldsType) => {
            initialValue[fieldsType] = calculateDefaultValue(realFields);
        });

        return initialValue;
    }, [fields]);

    const [filtersChanged, setFiltersChanged] = React.useState<boolean>(true);
    const [savedFilters, setSavedFilters] = React.useState<Array<TableFilterType>>([]);
    const [savedFavoriteFilters, setSavedFavoriteFilters] = React.useState<Array<TableFilterType>>(
        []
    );

    const [tableFilters, setTableFilters] = React.useState<Array<FilterType>>(() => {
        const lastFilterSaved = storage.get("last-filter-saved-" + filterGroup);

        if (lastFilterSaved) {
            return lastFilterSaved.tableFilters;
        } else {
            return createTableFilters(fields[defaultFilterType], defaultValue()[defaultFilterType]);
        }
    });
    const [type, setType] = React.useState<string>(() => {
        const lastFilterSaved = storage.get("last-filter-saved-" + filterGroup);

        if (lastFilterSaved) {
            return lastFilterSaved.type;
        } else {
            return defaultFilterType;
        }
    });
    const [form, setForm] = React.useState<FormType>(() => {
        const lastFilterSaved = storage.get("last-filter-saved-" + filterGroup);

        if (lastFilterSaved) {
            return lastFilterSaved.form;
        } else {
            return defaultValue();
        }
    });

    const [searchIndex, setSearchIndex] = React.useState<number>(0);

    const [partialListRequest, setPartialListRequest] = React.useState<{
        searchIndex: number;
        filters: Array<FilterType>;
    }>(() => {
        return {
            searchIndex: searchIndex,
            filters: tableFilters,
        };
    });

    const [realTimeTableFilters, setRealTimeTableFilters] = React.useState<Array<FilterType>>([]);
    const [realTimeTableFiltersDescription, setRealTimeTableFiltersDescription] =
        React.useState<string>("");

    // Funzione per caricare i filtri dal backend
    const loadTableFilter = React.useCallback((): AbortController => {
        const [apiCall, abortController] = getSavedTableFilter({ filterGroup, size: 10 });

        apiCall.then((response) => {
            const tmpFilters = response.map((item) => {
                return {
                    ...item,
                    filters: JSON.parse(item.filters),
                };
            });

            setSavedFilters(tmpFilters.filter((item) => !item.favorite));

            setSavedFavoriteFilters(tmpFilters.filter((item) => item.favorite));
        });

        return abortController;
    }, [getSavedTableFilter, filterGroup]);

    // Salvataggio di un filtro sul backend
    const addTableFilter = React.useCallback(
        (filters: any) => {
            if (typeof filters === "object" && filters !== null && _.size(filters) > 0) {
                let validField = 0;

                _.forEach(filters, (value) => {
                    if (value) {
                        validField++;
                    }
                });

                if (validField > 0) {
                    const [apiCall] = createTableFilter({
                        filter_group: filterGroup,
                        filter_type: type,
                        filters: JSON.stringify(filters),
                        version: 1,
                    });

                    apiCall.then(() => {
                        loadTableFilter();
                    });
                }
            }
        },
        [createTableFilter, loadTableFilter, filterGroup, type]
    );

    // Quando uno clicca sulla stellina
    const setFavorite = React.useCallback(
        (id: string, favorite: boolean) => {
            const [apiCall] = setFavoriteTableFilter({ id }, { favorite: favorite });

            apiCall.then(() => {
                loadTableFilter();
            });
        },
        [setFavoriteTableFilter, loadTableFilter]
    );

    // Per eliminare una ricerca
    const deleteFilter = React.useCallback(
        (id: string) => {
            const [apiCall] = deleteTableFilter({ id });

            apiCall.then(() => {
                loadTableFilter();
            });
        },
        [deleteTableFilter, loadTableFilter]
    );

    // Caricamento dei filtri salvati
    React.useEffect(() => {
        const abortController = loadTableFilter();

        return () => {
            abortController.abort();
        };
    }, [loadTableFilter]);

    const onPartialSearchClick = React.useCallback(
        (newForm: FormType, typeToUse: string, saveFilter: boolean) => {
            const filters: Array<FilterType> = createTableFilters(
                fields[typeToUse],
                newForm[typeToUse]
            );

            if (filters.length && filtersChanged && saveFilter) {
                addTableFilter(newForm[typeToUse]);

                setFiltersChanged(false);
            }

            setType(typeToUse);

            setTableFilters(filters);

            setSearchIndex((prevSearchIndex) => prevSearchIndex + 1);

            // Salvo sul browser l'ultima ricerca effettuata per poterla recuperare quando rietro sulla pagina
            storage.set("last-filter-saved-" + filterGroup, {
                type: typeToUse,
                tableFilters: filters,
                form: newForm,
            });
        },
        [fields, addTableFilter, filterGroup, filtersChanged]
    );

    // Quando uno clicca sul bottone salva creo l'array dei filtri da inoltrare alla tabella
    // contestualmente provo a salvare i filtri
    const onSearchClick = React.useCallback(() => {
        onPartialSearchClick(form, type, true);
    }, [onPartialSearchClick, form, type]);

    const onResetClick = React.useCallback(
        (typeToSet?: string) => {
            const newForm = defaultValue();

            setForm(newForm);

            onPartialSearchClick(newForm, typeToSet ?? type, false);
        },
        [defaultValue, onPartialSearchClick, type]
    );

    // Quando uno clicca su un filtro salvato viene settato il form di ricerca
    const setFilter = React.useCallback(
        (filterType: string, filtersToImport: any) => {
            const newForm = {
                ...defaultValue(),
                [filterType]: filtersToImport,
            };

            setForm(newForm);

            onPartialSearchClick(newForm, filterType, false);
        },
        [onPartialSearchClick, defaultValue]
    );

    // Quando viene modificato un campo setto il valore nel form e abilito il salvataggio dei filtri nel backend
    const onInputChange = React.useCallback(
        (key: string, value: any) => {
            setFiltersChanged(true);

            setForm((prevForm: FormType) => ({
                ...prevForm,
                [type]: { ...prevForm[type], [key]: value },
            }));
        },
        [type]
    );

    React.useEffect(() => {
        setRealTimeTableFiltersDescription(
            calculateFiltersDescription(fields[type], form[type], true).join(", ")
        );

        setRealTimeTableFilters(createTableFilters(fields[type], form[type]));
    }, [fields, form, type, calculateFiltersDescription]);

    React.useEffect(() => {
        setPartialListRequest({ searchIndex: searchIndex, filters: tableFilters });
    }, [searchIndex, tableFilters]);

    return {
        partialListRequest,
        type,
        savedFilters,
        savedFavoriteFilters,
        form,
        realTimeTableFilters,
        realTimeTableFiltersDescription,
        filtersChanged,
        setType: onResetClick,
        setFavorite,
        deleteFilter,
        onInputChange,
        setFilter,
        onResetClick,
        onSearchClick,
    };
}

export default useTableFilterBase;
